import { AxiosError } from 'axios';

/**
 * API 错误响应接口
 */
interface APIErrorResponse {
    code?: string;
    message?: string;
    error?: string;
    detail?: string;
}

/**
 * 错误消息映射表
 * 用于将常见的英文错误消息转换为中文
 */
const ERROR_MESSAGE_MAP: Record<string, string> = {
    'Network Error': '网络连接失败，请检查网络设置',
    'timeout': '请求超时，请稍后重试',
    'Request failed with status code 400': '请求参数错误',
    'Request failed with status code 401': '未授权，请检查 API 密钥',
    'Request failed with status code 403': '访问被拒绝',
    'Request failed with status code 404': '请求的资源不存在',
    'Request failed with status code 413': '文件太大，请选择较小的文件',
    'Request failed with status code 500': '服务器内部错误，请稍后重试',
    'Request failed with status code 502': '服务器网关错误，请稍后重试',
    'Request failed with status code 503': '服务暂时不可用，请稍后重试',
};

/**
 * 默认错误消息
 */
const DEFAULT_ERROR_MESSAGES: Record<string, string> = {
    merge: '合并失败，请重试',
    split: '拆分失败，请重试',
    compress: '压缩失败，请重试',
    delete: '删除失败，请重试',
    convert: '转换失败，请重试',
    default: '操作失败，请重试',
};

/**
 * 从 Axios 错误中提取错误消息
 */
function extractAxiosErrorMessage(error: AxiosError<APIErrorResponse>): string | null {
    // 尝试从响应数据中提取错误消息
    if (error.response?.data) {
        const data = error.response.data;
        if (data.code && data.message) {
            // 解析统一的错误格式 { code, message, detail }
            const detailStr = data.detail ? ` (${data.detail})` : '';
            return `[${data.code}] ${data.message}${detailStr}`;
        }
        if (data.message) return data.message;
        if (data.error) return data.error;
        if (data.detail) return data.detail;
    }

    // 尝试从错误消息映射表中获取
    if (error.message) {
        const mappedMessage = ERROR_MESSAGE_MAP[error.message];
        if (mappedMessage) return mappedMessage;
    }

    // 检查状态码相关的错误消息
    if (error.response?.status) {
        const statusMessage = ERROR_MESSAGE_MAP[`Request failed with status code ${error.response.status}`];
        if (statusMessage) return statusMessage;
    }

    // 检查网络错误
    if (error.code === 'ERR_NETWORK') {
        return ERROR_MESSAGE_MAP['Network Error'];
    }

    // 检查超时
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return ERROR_MESSAGE_MAP['timeout'];
    }

    return null;
}

/**
 * 从普通 Error 对象中提取错误消息
 */
function extractErrorMessage(error: Error): string | null {
    if (error.message) {
        // 检查是否有映射的错误消息
        const mappedMessage = ERROR_MESSAGE_MAP[error.message];
        if (mappedMessage) return mappedMessage;

        // 返回原始错误消息
        return error.message;
    }
    return null;
}

/**
 * 获取用户友好的错误消息
 * 
 * @param error - 错误对象，可以是 AxiosError、Error 或其他未知类型
 * @param operation - 操作类型，用于提供默认错误消息
 * @returns 用户友好的错误消息
 * 
 * @example
 * try {
 *   await pdfService.mergePDFs({ files });
 * } catch (error) {
 *   setError(getErrorMessage(error, 'merge'));
 * }
 */
export function getErrorMessage(
    error: unknown,
    operation: 'merge' | 'split' | 'compress' | 'delete' | 'convert' | 'default' = 'default'
): string {
    // 获取默认错误消息
    const defaultMessage = DEFAULT_ERROR_MESSAGES[operation] || DEFAULT_ERROR_MESSAGES.default;

    // 处理 null 或 undefined
    if (error == null) {
        return defaultMessage;
    }

    // 处理 AxiosError
    if (isAxiosError(error)) {
        const message = extractAxiosErrorMessage(error);
        if (message) return message;
        return defaultMessage;
    }

    // 处理普通 Error
    if (error instanceof Error) {
        const message = extractErrorMessage(error);
        if (message) return message;
        return defaultMessage;
    }

    // 处理字符串类型的错误
    if (typeof error === 'string') {
        return error || defaultMessage;
    }

    // 处理包含 message 属性的对象
    if (typeof error === 'object' && 'message' in error) {
        const message = (error as { message: unknown }).message;
        if (typeof message === 'string' && message) {
            return message;
        }
    }

    // 返回默认错误消息
    return defaultMessage;
}

/**
 * 类型守卫：判断是否为 AxiosError
 */
function isAxiosError(error: unknown): error is AxiosError<APIErrorResponse> {
    return (
        typeof error === 'object' &&
        error !== null &&
        'isAxiosError' in error &&
        (error as AxiosError).isAxiosError === true
    );
}

/**
 * 日志记录错误（用于开发调试）
 */
export function logError(error: unknown, context?: string): void {
    const prefix = context ? `[${context}]` : '[Error]';

    if (isAxiosError(error)) {
        console.error(prefix, 'Axios Error:', {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data,
            url: error.config?.url,
        });
    } else if (error instanceof Error) {
        console.error(prefix, error.message, error.stack);
    } else {
        console.error(prefix, error);
    }
}
